/**
 * ThemeContext — gestisce il tema UI (dark/light) a livello globale.
 * - Legge/scrive il tema preferito dell'utente tramite /api/auth/me
 * - Applica la classe "light" su <html> (Tailwind usa darkMode:class; absence of "light" = dark)
 * - Supporta anche utenti non loggati con fallback localStorage
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ThemeContext = createContext({ theme: 'dark', setTheme: () => {} });

/**
 * Determina se la rotta corrente è "tematizzabile" (CRM agenzie o Dashboard cliente).
 * Il sito web pubblico e tutte le landing NON devono mai essere convertite.
 */
const isThemedPath = (pathname) => {
  if (!pathname) return false;
  return pathname.startsWith('/crm') || pathname.startsWith('/dashboard') || pathname.startsWith('/immobiliare');
};

const applyThemeToDocument = (theme, pathname) => {
  const html = document.documentElement;
  if (theme === 'light' && isThemedPath(pathname)) {
    html.classList.add('light');
  } else {
    html.classList.remove('light');
  }
};

export const ThemeProvider = ({ children }) => {
  const location = useLocation();
  const [theme, setThemeState] = useState(() => {
    try {
      return localStorage.getItem('theme_preference') || 'dark';
    } catch (_) {
      return 'dark';
    }
  });

  // Applica/rimuove la classe "light" ogni volta che cambia tema o rotta
  useEffect(() => {
    applyThemeToDocument(theme, location.pathname);
  }, [theme, location.pathname]);

  // Sync con backend al mount se autenticato
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
        if (!r.ok) return;
        const me = await r.json();
        const serverPref = me?.theme_preference || 'dark';
        setThemeState(serverPref);
        try { localStorage.setItem('theme_preference', serverPref); } catch (_) { /* noop */ }
      } catch (_) { /* non loggato */ }
    })();
  }, []);

  const setTheme = useCallback(async (nextTheme) => {
    if (nextTheme !== 'dark' && nextTheme !== 'light') return;
    setThemeState(nextTheme);
    try { localStorage.setItem('theme_preference', nextTheme); } catch (_) { /* noop */ }
    try {
      await fetch(`${API_URL}/api/auth/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ theme_preference: nextTheme }),
      });
    } catch (_) { /* silent */ }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
